import React, { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Zap, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PACKS = [
  { id: "credits_500", usd: 5, credits: 500, label: "Starter" },
  { id: "credits_1200", usd: 10, credits: 1200, label: "Racer", tag: "20% bonus" },
  { id: "credits_2600", usd: 20, credits: 2600, label: "Pro", tag: "30% bonus", best: true },
  { id: "credits_7000", usd: 50, credits: 7000, label: "Champion", tag: "40% bonus" },
];

export default function BuyCredits() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);

  const buy = async (pack) => {
    setBusy(pack.id);
    try {
      const { data } = await api.post("/payments/credits/checkout", {
        package_id: pack.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <div className="animate-slide-up space-y-8" data-testid="buy-credits-page">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <Zap className="text-race-red" /> BUY CREDITS
        </h1>
        <p className="text-race-sub text-sm mt-2">
          Credits fuel contest entries. {user && <span className="text-race-txt">Current balance: {(user.credits || 0).toLocaleString()} credits</span>}
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PACKS.map((p) => (
          <div key={p.id}
            className={`relative bg-race-surface border p-6 flex flex-col ${p.best ? "border-race-red" : "border-race-line"}`}
            data-testid={`credit-pack-${p.id}`}>
            {p.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-race-red text-race-bg text-[10px] font-mono uppercase px-2 py-0.5 rounded-full">Best Value</span>}
            <div className="overline mb-1">{p.label}</div>
            <div className="font-heading text-4xl tracking-tight">{p.credits.toLocaleString()}</div>
            <div className="text-race-sub text-xs mb-4">credits</div>
            {p.tag && <div className="flex items-center gap-1 text-green-400 text-xs mb-4"><Check size={12} /> {p.tag}</div>}
            <div className="mt-auto">
              <div className="font-heading text-2xl mb-3">${p.usd}</div>
              <button onClick={() => buy(p)} disabled={busy === p.id} data-testid={`buy-${p.id}`}
                className="w-full flex items-center justify-center gap-2 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-mono text-xs uppercase tracking-wide py-3 rounded-sm transition-colors">
                <CreditCard size={15} /> {busy === p.id ? "…" : "Buy"}
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-race-muted text-xs">Secure checkout by Stripe. Test mode - use card 4242 4242 4242 4242, any future expiry & CVC.</p>
    </div>
  );
}
